/**
 * Wann der PCM-Weg aufgibt. (02.10.2026, Ticket 2919; Nachprüfung am selben Tag)
 *
 * Der Strom-Weg von September stotterte, und es fiel erst nach Tagen auf
 * (Journal 22.–25.09.: erste Stücke 71 von 86 gestört). Diesmal zählt der
 * Player selbst mit und schaltet ab, bevor Martin es merkt:
 *
 *  - Jedes Stück, das gespielt wurde, wird gezählt — mit der Frage, ob sein
 *    Vorrat je leer lief (Lücke ≥ 100 ms, `AUSSETZER_MS` in tts-pcm.ts).
 *  - FRÜH (der Totalausfall darf nicht neun stotternde Antworten kosten):
 *      · zwei gestörte ERSTE Stücke hintereinander (dort ist der Vorrat am
 *        dünnsten, dort stottert es zuerst), oder
 *      · 3 gestörte unter den letzten 5 Stücken.
 *  - SPÄT, für den schleichenden Fall: ≥ 5 % gestörte in einem gleitenden
 *    Fenster der letzten 50 Stücke (erst, wenn das Fenster voll ist).
 *  - Fehler vor dem ersten Paket (Netz, 5xx, gesperrter Kontext, Frist): zwei
 *    binnen zehn Minuten genügen. Ein sauber gespieltes Stück setzt den Zähler
 *    zurück — zwei vereinzelte iOS-Unterbrechungen am Tag schalten nicht ab.
 *    Jedes einzelne Stück geht sofort den Datei-Weg (das macht die
 *    Warteschlange); hier geht es um den Rest der Sitzung.
 *  - Der Rückfall gilt für den Rest dieser Tab-Sitzung (sessionStorage) und
 *    für ALLE folgenden Antworten. Die Einstellungen zeigen ihn mit Grund
 *    an; Schalter aus- und wieder einschalten setzt ihn zurück
 *    (`pcmRueckfallZuruecksetzen`).
 *
 * Eine Absage des Servers (409: Datei liegt schon da, Pegel noch nicht
 * gemessen, …) ist KEIN Fehler und zählt nicht.
 */

import { pcmGedaechtnis } from "./tts-pcm-gedaechtnis.js";

/** Spät: ab diesem Anteil gestörter Stücke im Fenster wird abgeschaltet … */
const RUECKFALL_ANTEIL = 0.05;
/** … sobald das Fenster mit so vielen Stücken voll ist. */
export const RUECKFALL_FENSTER = 50;
/** Früh: so viele der letzten … */
const RUECKFALL_KURZ_FENSTER = 5;
/** … gestört lösen aus. */
const RUECKFALL_KURZ_GESTOERT = 3;
/** Früh: so viele gestörte ERSTE Stücke hintereinander lösen aus. */
const RUECKFALL_ERSTE_FOLGE = 2;
/** So viele Fehler vor dem ersten Paket genügen … */
const RUECKFALL_MAX_FEHLER = 2;
/** … wenn sie binnen dieser Zeit (ms) liegen. */
export const RUECKFALL_FEHLER_GILT_MS = 10 * 60_000;

const SCHLUESSEL = "voice-v8:pcm-rueckfall";

interface RueckfallStand {
  /** Stücke im gleitenden Fenster. */
  stuecke: number;
  /** Davon gestört. */
  gestoert: number;
  /** Gestörte erste Stücke in Folge. */
  ersteFolge: number;
  /** Fehler vor dem ersten Paket in den letzten zehn Minuten. */
  fehler: number;
  aktiv: boolean;
  grund: string | null;
}

export class PcmRueckfall {
  private fenster: boolean[] = [];
  private ersteFolge = 0;
  private fehlerZeiten: number[] = [];
  private aktivGrund: string | null = null;
  private istAktiv = false;

  constructor(private readonly speicher: Pick<Storage, "getItem" | "setItem" | "removeItem"> | null = holeSpeicher()) {
    try {
      const roh = this.speicher?.getItem(SCHLUESSEL);
      if (roh) {
        const j = JSON.parse(roh) as { aktiv?: unknown; grund?: unknown };
        if (j.aktiv === true) {
          this.istAktiv = true;
          this.aktivGrund = typeof j.grund === "string" ? j.grund : "gemerkt";
        }
      }
    } catch {
      /* ohne Speicher gilt der Rückfall nur bis zum Neuladen */
    }
  }

  aktiv(): boolean {
    return this.istAktiv;
  }

  stand(jetzt = Date.now()): Readonly<RueckfallStand> {
    return {
      stuecke: this.fenster.length,
      gestoert: this.fenster.filter(Boolean).length,
      ersteFolge: this.ersteFolge,
      fehler: this.fehlerZeiten.filter((t) => jetzt - t < RUECKFALL_FEHLER_GILT_MS).length,
      aktiv: this.istAktiv,
      grund: this.aktivGrund,
    };
  }

  /**
   * Ein gespieltes Stück. `erstes`: erstes Stück seiner Antwort. `gestoert`:
   * der Vorrat lief leer. Gibt den Grund zurück, wenn DIESES Stück den Rückfall
   * auslöst — sonst null.
   */
  stueck(erstes: boolean, gestoert: boolean): string | null {
    this.fenster.push(gestoert);
    while (this.fenster.length > RUECKFALL_FENSTER) this.fenster.shift();
    // Ein sauberes Stück: die vereinzelten Fehler davor gelten nicht mehr als Serie.
    if (!gestoert) this.fehlerZeiten = [];
    if (erstes) this.ersteFolge = gestoert ? this.ersteFolge + 1 : 0;
    if (this.istAktiv) return null;
    if (this.ersteFolge >= RUECKFALL_ERSTE_FOLGE) {
      return this.schalteAb(`${this.ersteFolge} gestörte erste Stücke hintereinander`);
    }
    const kurz = this.fenster.slice(-RUECKFALL_KURZ_FENSTER);
    const kurzGestoert = kurz.filter(Boolean).length;
    if (kurz.length >= RUECKFALL_KURZ_FENSTER && kurzGestoert >= RUECKFALL_KURZ_GESTOERT) {
      return this.schalteAb(`${kurzGestoert} von den letzten ${RUECKFALL_KURZ_FENSTER} Stücken gestört`);
    }
    const gestoertGesamt = this.fenster.filter(Boolean).length;
    if (this.fenster.length >= RUECKFALL_FENSTER && gestoertGesamt / this.fenster.length >= RUECKFALL_ANTEIL) {
      return this.schalteAb(`${gestoertGesamt} von den letzten ${this.fenster.length} Stücken gestört`);
    }
    return null;
  }

  /** Ein Fehler vor dem ersten Paket. Gibt den Grund zurück, wenn er den Rückfall auslöst. */
  fehlerVorStart(grund: string, jetzt = Date.now()): string | null {
    this.fehlerZeiten = this.fehlerZeiten.filter((t) => jetzt - t < RUECKFALL_FEHLER_GILT_MS);
    this.fehlerZeiten.push(jetzt);
    if (this.istAktiv) return null;
    if (this.fehlerZeiten.length >= RUECKFALL_MAX_FEHLER) {
      return this.schalteAb(`${this.fehlerZeiten.length} Fehler vor dem ersten Paket (${grund.slice(0, 40)})`);
    }
    return null;
  }

  private schalteAb(grund: string): string {
    this.istAktiv = true;
    this.aktivGrund = grund;
    try {
      this.speicher?.setItem(SCHLUESSEL, JSON.stringify({ aktiv: true, grund }));
    } catch {
      /* Speicher gesperrt — der Rückfall gilt trotzdem für diese Seite */
    }
    return grund;
  }

  /** Neu anfangen — der Schalter wurde aus- und wieder eingeschaltet. */
  zuruecksetzen(): void {
    this.fenster = [];
    this.ersteFolge = 0;
    this.fehlerZeiten = [];
    this.istAktiv = false;
    this.aktivGrund = null;
    try {
      this.speicher?.removeItem(SCHLUESSEL);
    } catch {
      /* egal */
    }
  }
}

function holeSpeicher(): Storage | null {
  try {
    return typeof sessionStorage === "undefined" ? null : sessionStorage;
  } catch {
    return null;
  }
}

/** Die eine Buchführung dieser Seite. */
export const pcmRueckfall = new PcmRueckfall();

export function pcmRueckfallZuruecksetzen(): void {
  pcmRueckfall.zuruecksetzen();
  // Der Schalter wurde aus- und wieder eingeschaltet: auch das zeitlich begrenzte Störungs-Gedächtnis fängt neu an.
  pcmGedaechtnis.zuruecksetzen();
}
